import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useGraphEditorStore } from '@/graph-store';
import { useOperationCatalog } from '@/api/useOperationCatalog';
import type { AddStepFormState } from './types';
import type { Archetype, OperationTypeDefinition, Duration } from '@/graph/domain/types';
import type { TransferFormState } from '@/graph-store/actions/transferHelpers';
import { topologyClassOf, type ConversionExtras, type ConversionPlan } from '@/graph-store/actions/archetypeConversion';
import { formToOperationSpec } from './formToSpec';
import { buildOperationTemplate } from './buildOperationTemplate';
import { ArchetypeChangeDialog } from './ArchetypeChangeDialog';
import { buildEditFormState, resolveCatalogEntryForEdit } from './editPrepopulation';
import { StepDetailsPanel } from './StepDetailsPanel';
import type { StepValidationFocusTarget } from './StepDetailsPanel';
import { TransferDetailsPanel } from './TransferDetailsPanel';
import { ComplexDetailsPanel, type ComplexFormState } from './ComplexDetailsPanel';
import { StepSidePanel } from './StepSidePanel';
import { OperationsToolbox } from './OperationsToolbox';
import { __addStepModalInternal } from './AddStepModal';

function sameStringArray(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

/**
 * Extract a single numeric value from raw data for range validation
 */
function extractRangeScalarValue(raw: unknown): number | string {
  if (raw == null) return '';
  if (typeof raw === 'string' || typeof raw === 'number') {
    const n = typeof raw === 'number' ? raw : Number(raw);
    return Number.isFinite(n) ? n : '';
  }
  if (typeof raw === 'object' && !Array.isArray(raw)) {
    const obj = raw as Record<string, unknown>;
    if (obj.value != null) return extractRangeScalarValue(obj.value);
    if (obj.amount != null) return extractRangeScalarValue(obj.amount);
    if (obj.label != null) return extractRangeScalarValue(obj.label);
  }
  return '';
}

/**
 * Validate all range parameters against their min/max bounds
 * Returns error message if validation fails, null if all pass
 */
function validateRangeParameters(
  parameterValues: Record<string, unknown>,
  parameterDefs: any[]
): string | null {
  const errors: string[] = [];

  parameterDefs.forEach((def: any) => {
    const paramName = def.parameterName;
    
    // Check direct range parameters
    if (def.fieldType === 'range' && paramName) {
      const value = parameterValues[paramName];
      
      if (value === null || value === undefined || value === '') {
        return;
      }

      const scalar = extractRangeScalarValue(value);
      if (scalar === '') {
        return;
      }

      const numValue = typeof scalar === 'number' ? scalar : Number(scalar);
      if (!Number.isFinite(numValue)) {
        return;
      }

      const min = def.rangeMin;
      const max = def.rangeMax;

      if (min != null && numValue < min) {
        errors.push(`${paramName}: value ${numValue} is below minimum (${min})`);
      }
      if (max != null && numValue > max) {
        errors.push(`${paramName}: value ${numValue} is above maximum (${max})`);
      }
    }

    // Check child parameters in groups
    const children = def.paramValues || def.children || def.subMenu || [];
    if (Array.isArray(children)) {
      children.forEach((child: any) => {
        if (child.fieldType === 'range') {
          const childParamName = child.parameterName;
          
          // For group fields, the value can be stored in two ways:
          // 1. Under the group object: parameterValues[groupName] = { childName: value }
          // 2. Directly by full path: parameterValues[groupName.childName]
          
          let childValue: unknown = undefined;

          // Prefer group-scoped values first to avoid collisions with top-level params
          if (paramName) {
            const groupValueRaw = parameterValues[paramName];
            const groupValue = unwrapMandatoryValue(groupValueRaw);
            if (groupValue && typeof groupValue === 'object' && !Array.isArray(groupValue)) {
              childValue = (groupValue as Record<string, unknown>)[childParamName] ??
                           (groupValue as Record<string, unknown>)[child.safeKey] ??
                           (groupValue as Record<string, unknown>)[child.name] ??
                           (groupValue as Record<string, unknown>)[child.label];
            }
          }

          // Legacy fallback: fully-qualified group path only
          if ((childValue === null || childValue === undefined) && paramName) {
            childValue = parameterValues[`${paramName}.${childParamName}`];
          }
          
          if (childValue === null || childValue === undefined || childValue === '') {
            return;
          }

          const scalar = extractRangeScalarValue(childValue);
          if (scalar === '') {
            return;
          }

          const numValue = typeof scalar === 'number' ? scalar : Number(scalar);
          if (!Number.isFinite(numValue)) {
            return;
          }

          const childMin = child.rangeMin;
          const childMax = child.rangeMax;
          const displayName = `${paramName}.${childParamName}`;

          if (childMin != null && numValue < childMin) {
            errors.push(`${displayName}: value ${numValue} is below minimum (${childMin})`);
          }
          if (childMax != null && numValue > childMax) {
            errors.push(`${displayName}: value ${numValue} is above maximum (${childMax})`);
          }
        }
      });
    }
  });

  return errors.length > 0 ? errors.join('\n') : null;
}

function unwrapMandatoryValue(raw: unknown, depth = 0): unknown {
  if (depth > 10) return raw;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return raw;
  const obj = raw as Record<string, unknown>;
  if ((obj.parameterId != null || obj.parameterName != null) && obj.value !== undefined) {
    return unwrapMandatoryValue(obj.value, depth + 1);
  }
  return raw;
}

function readFirstDefined(obj: Record<string, unknown>, keys: Array<string | undefined>): unknown {
  for (const key of keys) {
    if (!key) continue;
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      return obj[key];
    }
  }
  return undefined;
}

function isEmptyScalar(raw: unknown): boolean {
  const value = unwrapMandatoryValue(raw);
  if (value == null) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (typeof value === 'number') return !Number.isFinite(value);
  if (Array.isArray(value)) return value.length === 0;
  return false;
}

function usesUnitPair(def: any): boolean {
  const tags = Array.isArray(def?.parameterTags) ? def.parameterTags : [];
  return def?.fieldType === 'units' || tags.some((tag: unknown) => String(tag || '').trim() !== '' && String(tag).toLowerCase() !== 'none');
}

function readUnitAndAmount(raw: unknown): { amount: unknown; unit: string } {
  const value = unwrapMandatoryValue(raw);

  if (value == null) return { amount: '', unit: '' };
  if (typeof value === 'string' || typeof value === 'number') return { amount: value, unit: '' };
  if (typeof value !== 'object' || Array.isArray(value)) return { amount: '', unit: '' };

  const obj = value as Record<string, unknown>;
  const nested =
    (obj.amount && typeof obj.amount === 'object' && !Array.isArray(obj.amount)
      ? (obj.amount as Record<string, unknown>)
      : null) ??
    (obj.value && typeof obj.value === 'object' && !Array.isArray(obj.value)
      ? (obj.value as Record<string, unknown>)
      : null);

  if (nested) {
    return {
      amount: nested.amount ?? nested.value ?? '',
      unit: String(nested.unit ?? nested.units ?? obj.unit ?? obj.units ?? '').trim(),
    };
  }

  return {
    amount: obj.amount ?? obj.value ?? '',
    unit: String(obj.unit ?? obj.units ?? '').trim(),
  };
}

function validateMandatoryParameters(
  parameterValues: Record<string, unknown>,
  parameterDefs: any[],
): string | null {
  const errors = collectMandatoryValidationErrors(parameterValues, parameterDefs);
  return errors.length > 0 ? errors.map((err) => err.message).join('\n') : null;
}

type MandatoryValidationError = {
  parameterName: string;
  fieldPath: string;
  message: string;
};

function collectMandatoryValidationErrors(
  parameterValues: Record<string, unknown>,
  parameterDefs: any[],
): MandatoryValidationError[] {
  const errors: MandatoryValidationError[] = [];
  const topLevelParamNames = new Set(
    parameterDefs
      .map((def: any) => String(def?.parameterName || def?.safeKey || def?.id || '').trim())
      .filter(Boolean),
  );

  parameterDefs.forEach((def: any) => {
    const paramName = String(def?.parameterName || def?.safeKey || def?.id || '').trim();
    if (!paramName) return;

    const required = Boolean(def?.required || def?.mandatory);
    const groupChildren = Array.isArray(def?.children)
      ? def.children
      : Array.isArray(def?.paramValues)
        ? def.paramValues
        : Array.isArray(def?.subMenu)
          ? def.subMenu
          : [];

    const rootValue = parameterValues[paramName];
    const normalizedRootValue = unwrapMandatoryValue(rootValue);

    if (required) {
      if (usesUnitPair(def)) {
        const pair = readUnitAndAmount(normalizedRootValue);
        if (isEmptyScalar(pair.amount)) {
          errors.push({ parameterName: paramName, fieldPath: paramName, message: `${paramName} is required` });
        } else if (!pair.unit) {
          errors.push({ parameterName: paramName, fieldPath: paramName, message: `${paramName} unit is required` });
        }
      } else if (isEmptyScalar(normalizedRootValue)) {
        errors.push({ parameterName: paramName, fieldPath: paramName, message: `${paramName} is required` });
      }
    }

    if (groupChildren.length === 0) return;

    const isMultipleGroup = Boolean(def?.allowMultiple);
    const groupRows = isMultipleGroup
      ? (Array.isArray(normalizedRootValue)
        ? normalizedRootValue
        : normalizedRootValue && typeof normalizedRootValue === 'object'
          ? [normalizedRootValue as Record<string, unknown>]
          : [])
      : [];
    const groupObject = !isMultipleGroup && normalizedRootValue && typeof normalizedRootValue === 'object' && !Array.isArray(normalizedRootValue)
      ? (normalizedRootValue as Record<string, unknown>)
      : null;

    groupChildren.forEach((child: any) => {
      if (!child?.required && !child?.mandatory) return;

      const childKey = String(child?.safeKey || child?.parameterName || child?.id || '').trim();
      if (!childKey) return;

      const resolveChildValue = (container?: Record<string, unknown> | null): unknown => {
        if (container) {
          const fromContainer = readFirstDefined(container, [childKey, child?.parameterName, child?.name, child?.label]);
          if (fromContainer !== undefined) return fromContainer;
        }

        // Avoid reading top-level parameter values when child names collide
        const childParamName = String(child?.parameterName || '').trim();
        const hasTopLevelCollision = topLevelParamNames.has(childKey) || (childParamName && topLevelParamNames.has(childParamName));
        return hasTopLevelCollision
          ? readFirstDefined(parameterValues, [`${paramName}.${childKey}`])
          : readFirstDefined(parameterValues, [`${paramName}.${childKey}`, childKey, child?.parameterName]);
      };

      const validateChild = (path: string, value: unknown) => {
        if (usesUnitPair(child)) {
          const pair = readUnitAndAmount(value);
          if (isEmptyScalar(pair.amount)) {
            errors.push({ parameterName: paramName, fieldPath: path, message: `${path} is required` });
          } else if (!pair.unit) {
            errors.push({ parameterName: paramName, fieldPath: path, message: `${path} unit is required` });
          }
          return;
        }
        if (isEmptyScalar(value)) {
          errors.push({ parameterName: paramName, fieldPath: path, message: `${path} is required` });
        }
      };

      if (isMultipleGroup && groupRows.length > 0) {
        groupRows.forEach((row, rowIndex) => {
          const childValue = resolveChildValue((row ?? {}) as Record<string, unknown>);
          validateChild(`${paramName}[${rowIndex}].${childKey}`, childValue);
        });
        return;
      }

      const childValue = resolveChildValue(groupObject);
      validateChild(`${paramName}.${childKey}`, childValue);
    });
  });

  return errors;
}

function findFirstMandatoryValidationTarget(
  parameterValues: Record<string, unknown>,
  parameterDefs: any[],
): StepValidationFocusTarget | null {
  const firstError = collectMandatoryValidationErrors(parameterValues, parameterDefs)[0];
  if (!firstError) return null;
  return {
    kind: 'parameter',
    parameterName: firstError.parameterName,
    fieldPath: firstError.fieldPath,
  };
}

export interface StepModalEditProps {
  operationId: string;
  onClose: () => void;
  onSaved?: (operationId: string) => void;
}

export function StepModalEdit({ operationId, onClose, onSaved }: StepModalEditProps) {
  const { data: catalog } = useOperationCatalog();
  const editStep = useGraphEditorStore((s) => s.editStep);
  const editTransferStep = useGraphEditorStore((s) => s.editTransferStep);
  const editComplexStep = useGraphEditorStore((s) => s.editComplexStep);
  const planOperationArchetypeChange = useGraphEditorStore((s) => s.planOperationArchetypeChange);
  const changeOperationArchetype = useGraphEditorStore((s) => s.changeOperationArchetype);
  const operation = useGraphEditorStore((s) => s.graphData.operationNodes[operationId]);
  const [pendingConversion, setPendingConversion] = useState<ConversionPlan | null>(null);
  const legacyRecommendedInstrument = Array.isArray(operation.recommendedInstrument)
    ? operation.recommendedInstrument
    : [];
  const [saveError, setSaveError] = useState<string | null>(null);
  const [validationFocusTarget, setValidationFocusTarget] = useState<StepValidationFocusTarget | null>(null);
  const [validationFocusRequestId, setValidationFocusRequestId] = useState(0);

  const initialForm = useMemo(() => {
    if (!operation || !catalog) return null;
    return buildEditFormState(operation, catalog);
  }, [operation, catalog]);

  const [form, setForm] = useState<AddStepFormState | null>(null);
  const transferFormRef = useRef<TransferFormState | null>(null);
  const [transferPreviewForm, setTransferPreviewForm] = useState<TransferFormState | null>(null);
  const complexFormRef = useRef<{ form: ComplexFormState; isValid: boolean } | null>(null);
  const [complexPreviewAdditionalInputIds, setComplexPreviewAdditionalInputIds] = useState<string[]>([]);

  // Initialize form once catalog loads
  const currentForm = form ?? initialForm;

  const selectedOp = useMemo(
    () => {
      if (!catalog) return null;
      return (
        catalog.find((o) => o.id === currentForm?.selectedOperationTypeId) ??
        resolveCatalogEntryForEdit(operation, catalog) ??
        null
      );
    },
    [catalog, currentForm?.selectedOperationTypeId, operation],
  );

  /**
   * The archetype the dialog is currently being edited *as*.
   *
   * The toolbox lets the user pick any operation in the catalog, so the stored
   * archetype stops being authoritative the moment they do. Every branch below
   * follows the selection — the same thing AddStepModal does — and `handleSave`
   * converts the operation when the two disagree.
   */
  const effectiveArchetype: Archetype = selectedOp?.operationType ?? operation.archetype;
  const isArchetypeChange = effectiveArchetype !== operation.archetype;

  const handleChange = useCallback((patch: Partial<AddStepFormState>) => {
    setForm((prev) => ({ ...(prev ?? initialForm!), ...patch }));
  }, [initialForm]);

  const handleParamChange = useCallback((key: string, value: unknown) => {
    setValidationFocusTarget(null);
    setForm((prev) => {
      const base = prev ?? initialForm!;
      return {
        ...base,
        parameterValues: { ...base.parameterValues, [key]: value },
      };
    });
  }, [initialForm]);

  const handleTransferFormChange = useCallback((form: TransferFormState) => {
    setValidationFocusTarget(null);
    transferFormRef.current = form;
    setTransferPreviewForm(form);
  }, []);

  const handleComplexFormChange = useCallback((complexForm: ComplexFormState, isValid: boolean) => {
    setValidationFocusTarget(null);
    complexFormRef.current = { form: complexForm, isValid };
    setComplexPreviewAdditionalInputIds((prev) =>
      sameStringArray(prev, complexForm.additionalInputIds ?? [])
        ? prev
        : [...(complexForm.additionalInputIds ?? [])],
    );
    setForm((prev) => {
      const base = prev ?? initialForm;
      if (!base) return prev;
      const next = {
        ...base,
        parameterValues: complexForm.parameterValues,
        durationHours: complexForm.durationHours,
        durationMinutes: complexForm.durationMinutes,
        durationSeconds: complexForm.durationSeconds,
        linkToPreviousStep: complexForm.linkToPreviousStep,
        linkedStepId: complexForm.linkedStepId,
        notes: complexForm.notes,
        recommendedInstrument: complexForm.recommendedInstrument,
        checkpoints: complexForm.checkpoints,
      };

      const unchanged =
        base.parameterValues === next.parameterValues &&
        base.durationHours === next.durationHours &&
        base.durationMinutes === next.durationMinutes &&
        base.durationSeconds === next.durationSeconds &&
        base.linkToPreviousStep === next.linkToPreviousStep &&
        base.linkedStepId === next.linkedStepId &&
        base.notes === next.notes &&
        base.recommendedInstrument === next.recommendedInstrument &&
        base.checkpoints === next.checkpoints;

      return unchanged ? base : next;
    });
  }, [initialForm]);

  const focusOnInvalidField = useCallback((target: StepValidationFocusTarget) => {
    setSaveError(null);
    setValidationFocusTarget(target);
    setValidationFocusRequestId((prev) => prev + 1);
  }, []);

  /**
   * Everything the conversion needs, gathered from whichever panel is on screen
   * for the *newly selected* archetype.
   */
  const buildConversionArgs = (): {
    patch: Record<string, unknown>;
    extras: ConversionExtras;
  } => {
    const operationTemplate = buildOperationTemplate(selectedOp!, currentForm!);
    const basePatch: Record<string, unknown> = {
      operationTypeId: selectedOp!.id,
      displayName: selectedOp!.operationName,
      operationTemplate,
      approvers: currentForm?.approvers,
      notifications: currentForm?.notifications,
    };

    if (effectiveArchetype === 'TRANSFER') {
      const tf = transferFormRef.current;
      const row = tf?.rows[0];
      return {
        patch: {
          ...basePatch,
          parameters: {},
          checkPoints: tf?.checkpoints ?? [],
          notes: tf?.notes || currentForm?.notes || undefined,
          recommendedInstrument: tf?.recommendedInstrument || undefined,
        },
        extras: {
          transferRow: row ?? undefined,
          destinationMaterialId: row?.destinationMaterialId ?? undefined,
          destinationDraft: row?.destinationDraft ?? undefined,
        },
      };
    }

    if (effectiveArchetype === 'COMPLEX') {
      const cf = complexFormRef.current?.form;
      return {
        patch: {
          ...basePatch,
          parameters: cf?.parameterValues ?? {},
          checkPoints: cf?.checkpoints ?? [],
          notes: cf?.notes || currentForm?.notes || undefined,
          recommendedInstrument: cf?.recommendedInstrument || undefined,
          duration: selectedOp!.durationAllow && cf
            ? {
                isInfinity: false,
                value: cf.durationHours * 3600 + cf.durationMinutes * 60 + cf.durationSeconds,
                linkToPreviousStep: cf.linkToPreviousStep,
                prevStep: cf.linkedStepId ?? undefined,
              }
            : undefined,
        },
        extras: { ioMapping: cf?.ioMapping ?? undefined },
      };
    }

    // TRANSFORM / MEASURE
    return {
      patch: { ...formToOperationSpec(currentForm!, selectedOp!), operationTemplate },
      extras: {},
    };
  };

  const runConversion = () => {
    const { patch, extras } = buildConversionArgs();
    try {
      changeOperationArchetype({
        operationId,
        target: {
          archetype: effectiveArchetype,
          operationTypeId: selectedOp!.id,
          displayName: selectedOp!.operationName,
          durationAllow: selectedOp!.durationAllow,
          checkpoints: selectedOp!.checkpoints,
        },
        extras,
        patch,
      });
    } catch (err) {
      setPendingConversion(null);
      setSaveError(err instanceof Error ? err.message : String(err));
      return;
    }
    setPendingConversion(null);
    onSaved?.(operationId);
    onClose();
  };

  const handleSave = () => {
    if (!selectedOp || !currentForm) return;

    setSaveError(null);

    // The archetype changed under the user — this is a conversion, not an edit.
    // Preview it first so anything destructive is confirmed before it happens.
    if (isArchetypeChange) {
      const { extras } = buildConversionArgs();
      const plan = planOperationArchetypeChange(
        operationId,
        {
          archetype: effectiveArchetype,
          operationTypeId: selectedOp.id,
          displayName: selectedOp.operationName,
          durationAllow: selectedOp.durationAllow,
          checkpoints: selectedOp.checkpoints,
        },
        extras,
      );

      if (plan.blockers.length > 0) {
        setSaveError(plan.blockers.join('\n'));
        return;
      }
      if (plan.isLossy || plan.materialsToDetach.length > 0 || plan.warnings.length > 0) {
        setPendingConversion(plan);
        return;
      }
      runConversion();
      return;
    }

    /**
     * The toolbox lets the user pick a different operation without changing the
     * archetype, so the patches below carry the new operationTypeId, parameters
     * and checkPoints. `editStep` / `editComplexStep` shallow-merge, which means
     * an operationTemplate left unrebuilt stays behind describing the *previous*
     * operation. Everything downstream reads parameter metadata off the template
     * — table columns above all — so the two have to move together.
     */
    const operationTemplate = buildOperationTemplate(selectedOp, currentForm);

    if (operation.archetype === 'TRANSFER' && transferFormRef.current) {
      // Use editTransferStep for transfer operations
      const tf = transferFormRef.current;
      const row = tf.rows[0];
      if (row) {
        editTransferStep({
          operationId,
          row,
          destinationMaterialId: row.destinationMaterialId ?? undefined,
          destinationDraft: row.destinationDraft ?? undefined,
          patch: {
            displayName: operation.displayName,
            notes: tf.notes || currentForm?.notes || undefined,
            recommendedInstrument: tf.recommendedInstrument || undefined,
            duration: (tf.durationHours || tf.durationMinutes || tf.durationSeconds)
              ? {
                  value: tf.durationHours * 3600 + tf.durationMinutes * 60 + tf.durationSeconds,
                  isInfinity: false,
                  linkToPreviousStep: tf.linkToPreviousStep,
                  prevStep: tf.linkedStepId ?? undefined,
                }
              : undefined,
            transferOptions: {
              quantityType: tf.quantityType,
              transferEntireVolume:
                row.amount === 0 || (row.amount != null && row.amount <= 0)
                  ? false
                  : tf.transferEntireVolume,
              sameVolume: tf.sameVolume,
              advancedOptionsEnabled: operation.transferOptions?.advancedOptionsEnabled ?? false,
            },
            checkPoints: tf.checkpoints || [],
            approvers: currentForm?.approvers,
            notifications: currentForm?.notifications,
          },
        });
      }
    } else if (operation.archetype === 'COMPLEX') {
      // Handle COMPLEX step editing with mandatory + range validation
      const cf = complexFormRef.current?.form;
      if (!cf) return;

      const mandatoryTarget = findFirstMandatoryValidationTarget(
        cf.parameterValues,
        selectedOp.parameters ?? [],
      );
      if (mandatoryTarget) {
        focusOnInvalidField(mandatoryTarget);
        return;
      }

      // Validate range parameters
      const rangeValidationError = validateRangeParameters(
        cf.parameterValues,
        selectedOp.parameters ?? [],
      );
      if (rangeValidationError) {
        setSaveError(`Range parameter validation failed: ${rangeValidationError}`);
        return;
      }

      const duration: Duration | undefined =
        selectedOp.durationAllow
          ? {
              isInfinity: false,
              value: cf.durationHours * 3600 + cf.durationMinutes * 60 + cf.durationSeconds,
              linkToPreviousStep: cf.linkToPreviousStep,
              prevStep: cf.linkedStepId ?? undefined,
            }
          : undefined;

      editComplexStep({
        operationId,
        sourceMaterialId,
        additionalInputIds: cf.additionalInputIds,
        outputMaterialIds: cf.outputMaterialIds,
        ioMapping: cf.ioMapping ?? [],
        patch: {
        operationTypeId: selectedOp.id,
        displayName: selectedOp.operationName,
        operationTemplate,
        parameters: cf.parameterValues,
        checkPoints: cf.checkpoints || [],
        duration,
        notes: cf.notes || currentForm?.notes || undefined,
        recommendedInstrument: cf.recommendedInstrument || undefined,
        approvers: currentForm?.approvers,
        notifications: currentForm?.notifications,
        },
      });
    } else {
      // Generic edit for TRANSFORM/MEASURE with range validation
      const mandatoryTarget = findFirstMandatoryValidationTarget(
        currentForm.parameterValues,
        selectedOp.parameters ?? [],
      );
      if (mandatoryTarget) {
        focusOnInvalidField(mandatoryTarget);
        return;
      }

      // Validate range parameters
      const rangeValidationError = validateRangeParameters(
        currentForm.parameterValues,
        selectedOp.parameters ?? [],
      );
      if (rangeValidationError) {
        setSaveError(`Range parameter validation failed: ${rangeValidationError}`);
        return;
      }

      const spec = { ...formToOperationSpec(currentForm, selectedOp), operationTemplate };
      editStep(operationId, spec);
    }

    onSaved?.(operationId);
    onClose();
  };

  const handleSelectOp = useCallback((op: OperationTypeDefinition) => {
    // Allow changing the operation in edit mode
    const nextArchetype = op.operationType ?? operation.archetype;
    const shapeChanged =
      topologyClassOf(nextArchetype) !== topologyClassOf(effectiveArchetype);

    // The transfer and complex panels keep their state in refs. Leaving a
    // previous archetype's ref populated would feed stale rows or IO mappings
    // into the save that follows.
    if (shapeChanged) {
      transferFormRef.current = null;
      complexFormRef.current = null;
      setTransferPreviewForm(null);
      setComplexPreviewAdditionalInputIds([]);
    }

    setSaveError(null);
    setValidationFocusTarget(null);
    setForm((prev) => ({
      ...(prev ?? currentForm)!,
      selectedOperationTypeId: op.id,
      parameterValues: {},
      checkpoints: op.checkpoints ?? [],
    }));
  }, [currentForm, effectiveArchetype, operation.archetype]);

  // Find source material from incoming edges
  const graphData = useGraphEditorStore.getState().graphData;
  const incomingEdges = Object.values(graphData.flowEdges).filter(
    (e) => e.targetId === operationId,
  );
  // Source is the non-destination input
  const oldDestId = operation.transferRow?.destinationMaterialId;
  const sourceEdge = incomingEdges.find((e) => e.sourceId !== oldDestId) ?? incomingEdges[0];
  const sourceMaterialId = sourceEdge?.sourceId ?? operation.transferRow?.sourceMaterialId ?? '';
  const sourceName = graphData.materialNodes[sourceMaterialId]?.name ?? '';
  // Build initial transfer form state for edit mode
  const transferInitialState = useMemo((): Partial<TransferFormState> | undefined => {
    if (effectiveArchetype !== 'TRANSFER') return undefined;
    // Converting *into* a transfer: no row to seed from, so the panel starts
    // blank exactly as it does in Add mode.
    const tr = operation.transferRow;
    if (!tr || operation.archetype !== 'TRANSFER') return undefined;

    const rowAmount = tr.quantity?.value?.amount ?? null;

    const row: import('@/graph-store/actions/transferHelpers').TransferRowFormState = {
      id: tr.id,
      mode: tr.symbolic ? 'symbolic' : 'numeric',
      amount: rowAmount,
      unit: tr.quantity?.value?.units ?? null,
      symbolic: tr.symbolic ?? null,
      destinationMaterialId: tr.destinationMaterialId ?? null,
      destinationDraft: null,
    };

    const durationTotal = operation.duration?.value ?? 0;
    return {
      quantityType: operation.transferOptions?.quantityType ?? 'Volume',
      transferEntireVolume: operation.transferOptions?.transferEntireVolume ?? false,
      sameVolume: operation.transferOptions?.sameVolume ?? false,
      rows: [row],
      notes: operation.notes ?? '',
      recommendedInstrument: legacyRecommendedInstrument,
      durationHours: Math.floor(durationTotal / 3600),
      durationMinutes: Math.floor((durationTotal % 3600) / 60),
      durationSeconds: durationTotal % 60,
      linkToPreviousStep: operation.duration?.linkToPreviousStep ?? false,
      linkedStepId: operation.duration?.prevStep ?? null,
      checkpoints: operation.checkPoints ?? [],
    };
  }, [operation]);

  const transferPreviewSeed = useMemo<TransferFormState | null>(() => {
    if (!transferInitialState) return null;
    return {
      quantityType: transferInitialState.quantityType ?? 'Volume',
      transferEntireVolume: transferInitialState.transferEntireVolume ?? false,
      sameVolume: transferInitialState.sameVolume ?? false,
      rows: transferInitialState.rows ?? [],
      notes: transferInitialState.notes ?? '',
      recommendedInstrument: transferInitialState.recommendedInstrument ?? [],
      durationHours: transferInitialState.durationHours ?? 0,
      durationMinutes: transferInitialState.durationMinutes ?? 0,
      durationSeconds: transferInitialState.durationSeconds ?? 0,
      linkToPreviousStep: transferInitialState.linkToPreviousStep ?? false,
      linkedStepId: transferInitialState.linkedStepId ?? null,
      checkpoints: transferInitialState.checkpoints ?? [],
    };
  }, [transferInitialState]);

  // Build initial complex form state for edit mode
  const complexInitialState = useMemo((): Partial<ComplexFormState> | undefined => {
    if (effectiveArchetype !== 'COMPLEX') return undefined;

    // Extract input material IDs from incoming edges
    const incomingEdges = Object.values(graphData.flowEdges).filter(
      (e) => e.targetId === operationId,
    );
    const additionalInputIds = incomingEdges
      .map((e) => e.sourceId)
      .filter((id) => id !== sourceMaterialId);

    // Extract output material IDs from outgoing edges
    const outgoingEdges = Object.values(graphData.flowEdges).filter(
      (e) => e.sourceId === operationId,
    );
    const mappedOutputIds = new Set(
      (operation.complexOptions?.ioMapping ?? []).map((m) => m.outputMaterialId),
    );
    const outputMaterialIds = outgoingEdges
      .map((e) => e.targetId)
      .filter((targetId) => {
        const material = graphData.materialNodes[targetId];
        if (!material) return false;
        if (material.isReminder || material.symbolicQuantity === 'remainder') return false;
        if (mappedOutputIds.size > 0) {
          return mappedOutputIds.has(targetId);
        }
        return true;
      });

    const durationTotal = operation.duration?.value ?? 0;
    return {
      additionalInputIds,
      outputMaterialIds,
      ioMapping: operation.complexOptions?.ioMapping ?? [],
      parameterValues: operation.parameters ?? {},
      durationHours: Math.floor(durationTotal / 3600),
      durationMinutes: Math.floor((durationTotal % 3600) / 60),
      durationSeconds: durationTotal % 60,
      notes: operation.notes ?? '',
      recommendedInstrument: legacyRecommendedInstrument,
      checkpoints: operation.checkPoints ?? [],
    };
  }, [operation, operationId, sourceMaterialId, graphData, legacyRecommendedInstrument, effectiveArchetype]);

  useEffect(() => {
    if (effectiveArchetype !== 'COMPLEX') return;
    if (complexFormRef.current || !complexInitialState) return;
    complexFormRef.current = {
      form: {
        additionalInputIds: complexInitialState.additionalInputIds ?? [],
        outputMaterialIds: complexInitialState.outputMaterialIds ?? [],
        ioMapping: complexInitialState.ioMapping ?? [],
        parameterValues: complexInitialState.parameterValues ?? {},
        durationHours: complexInitialState.durationHours ?? 0,
        durationMinutes: complexInitialState.durationMinutes ?? 0,
        durationSeconds: complexInitialState.durationSeconds ?? 0,
        linkToPreviousStep: complexInitialState.linkToPreviousStep ?? false,
        linkedStepId: complexInitialState.linkedStepId ?? null,
        notes: complexInitialState.notes ?? '',
        recommendedInstrument: complexInitialState.recommendedInstrument ?? [],
        checkpoints: complexInitialState.checkpoints ?? [],
      },
      isValid: true,
    };
    setComplexPreviewAdditionalInputIds(complexInitialState.additionalInputIds ?? []);
  }, [effectiveArchetype, complexInitialState]);

  const isTransfer = effectiveArchetype === 'TRANSFER';
  const isComplex = effectiveArchetype === 'COMPLEX';

  const renderCenterPanel = () => {
    if (!selectedOp || !currentForm) return null;

    if (isTransfer) {
      return (
        <TransferDetailsPanel
          sourceMaterialId={sourceMaterialId}
          operationType={selectedOp}
          onSave={() => handleSave()}
          onFormChange={handleTransferFormChange}
          initialState={transferInitialState}
        />
      );
    }

    if (isComplex) {
      return (
        <ComplexDetailsPanel
          sourceMaterialId={sourceMaterialId}
          operationType={selectedOp}
          onFormChange={handleComplexFormChange}
          initialState={complexInitialState}
          validationFocusTarget={validationFocusTarget}
          validationFocusRequestId={validationFocusRequestId}
        />
      );
    }

    // TRANSFORM / MEASURE
    return (
      <StepDetailsPanel
        operationType={selectedOp}
        form={currentForm}
        onChange={handleChange}
        onParamChange={handleParamChange}
        validationFocusTarget={validationFocusTarget}
        validationFocusRequestId={validationFocusRequestId}
      />
    );
  };

  if (!currentForm) return null;

  return (
    <>
    {pendingConversion && (
      <ArchetypeChangeDialog
        plan={pendingConversion}
        onConfirm={() => runConversion()}
        onCancel={() => setPendingConversion(null)}
      />
    )}
    <Dialog open onClose={onClose} maxWidth="xl" fullWidth sx={{ '& .MuiDialog-paper': { height: '80vh' } }}>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {isArchetypeChange ? 'Change Step Type' : 'Edit Step'}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ display: 'flex', gap: 0, p: 0, overflow: 'hidden' }}>
        {/* Left: Operations Toolbox (22%) - fully enabled in edit mode */}
        <Box sx={{ width: '22%', borderRight: 1, borderColor: 'divider', overflow: 'hidden' }}>
          <OperationsToolbox
            catalog={catalog ?? []}
            isLoading={false}
            archetypes={['TRANSFORM', 'MEASURE', 'TRANSFER', 'COMPLEX']}
            selectedId={currentForm.selectedOperationTypeId}
            onSelect={handleSelectOp}
          />
        </Box>

        {/* Center: Step Details (50%) */}
        <Box sx={{ width: '50%', borderRight: 1, borderColor: 'divider', overflow: 'auto' }}>
          {renderCenterPanel()}
        </Box>

        {/* Right: Side Panel (28%) */}
        <Box sx={{ width: '28%', overflow: 'hidden' }}>
          <StepSidePanel
            operationType={selectedOp}
            form={currentForm}
            onChange={handleChange}
            materialName={sourceName}
            operatorName=""
            transferForm={transferPreviewForm ?? transferPreviewSeed}
            sourceMaterialId={sourceMaterialId}
            complexAdditionalInputIds={complexPreviewAdditionalInputIds}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        {saveError && (
          <Box sx={{ flex: 1, color: 'error.main', fontSize: 12, pl: 2, whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxWidth: '400px' }}>
            {saveError}
          </Box>
        )}
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!selectedOp} onClick={handleSave}>
          {isArchetypeChange ? 'Change Step Type' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
}